import React, { useState, useMemo, useCallback } from 'react';
import AntLocalization from '../../utils/AntLocalization';

function formatDate(d) {
    if (!d) return '';
    var dd = d.getDate();
    var mm = d.getMonth() + 1;
    var yyyy = d.getFullYear();
    return (dd < 10 ? '0' : '') + dd + '/' + (mm < 10 ? '0' : '') + mm + '/' + yyyy;
}

function formatDateISO(d) {
    if (!d) return '';
    var yyyy = d.getFullYear();
    var mm = d.getMonth() + 1;
    var dd = d.getDate();
    return yyyy + '-' + (mm < 10 ? '0' : '') + mm + '-' + (dd < 10 ? '0' : '') + dd;
}

function daysBetween(a, b) {
    if (!a || !b) return 0;
    var msDay = 86400000;
    return Math.round(Math.abs(b.getTime() - a.getTime()) / msDay);
}

function isSameDay(a, b) {
    if (!a || !b) return false;
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

// ─── Range Mode Component ────────────────────────────────────
export default function RangeMode(props) {
    var {
        resources,
        bookings,
        durationType,
        resourceField,
        statusColors,
        statusField,
        onBookResource,
        onBookingClick
    } = props;

    var [checkIn, setCheckIn] = useState(null);
    var [checkOut, setCheckOut] = useState(null);

    var handleCheckInChange = useCallback(function (e) {
        var val = e.target.value;
        if (val) {
            var parts = val.split('-');
            setCheckIn(new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2])));
        } else {
            setCheckIn(null);
        }
    }, []);

    var handleCheckOutChange = useCallback(function (e) {
        var val = e.target.value;
        if (val) {
            var parts = val.split('-');
            setCheckOut(new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2])));
        } else {
            setCheckOut(null);
        }
    }, []);

    // Calculate duration label
    var durationLabel = useMemo(function () {
        if (!checkIn || !checkOut) return '';
        var days = daysBetween(checkIn, checkOut);
        if (days === 0) return '';

        if (durationType === 'HOURLY') {
            return days + ' ' + AntLocalization.translateText({ vi: 'ngày', en: 'day(s)', fr: 'jour(s)', es: 'día(s)', id: 'hari', ja: '日', zh: '天', ko: '일', hi: 'दिन', ar: 'يوم' });
        }
        if (durationType === 'MONTHLY') {
            var months = Math.round(days / 30 * 10) / 10;
            return months + ' ' + AntLocalization.translateText({ vi: 'tháng', en: 'month(s)', fr: 'mois', es: 'mes(es)', id: 'bulan', ja: 'か月', zh: '个月', ko: '개월', hi: 'महीना', ar: 'شهر' });
        }
        // DAILY (default - hotel)
        return days + ' ' + AntLocalization.translateText({ vi: 'đêm', en: 'night(s)', fr: 'nuits', es: 'noches', id: 'malam', ja: '泊', zh: '晚', ko: '박', hi: 'रातें', ar: 'ليالٍ' });
    }, [checkIn, checkOut, durationType]);

    // Determine which resources are available vs booked
    var resourceAvailability = useMemo(function () {
        if (!resources || !checkIn || !checkOut) return [];

        return resources.map(function (r) {
            var rCode = r.id || r.code;
            var isBooked = false;
            var conflictBooking = null;

            if (bookings && bookings.length > 0) {
                for (var i = 0; i < bookings.length; i++) {
                    var b = bookings[i];
                    // Match resource via resourceField prop or fallback
                    var bResource = '';
                    if (resourceField) {
                        bResource = b[resourceField] || '';
                    } else {
                        bResource = b.room_code || b.resource_code || b.serve_place_code || '';
                    }
                    if (bResource !== rCode) continue;

                    // Check status - skip cancelled
                    var status = statusField ? (b[statusField] || '') : '';
                    if (status === 'Cancel' || status === 'Cancelled') continue;

                    // Check date overlap
                    var bStart = b.check_in_date ? new Date(b.check_in_date) : null;
                    var bEnd = b.check_out_date ? new Date(b.check_out_date) : null;
                    if (!bStart || !bEnd) continue;

                    // Overlap: bStart < checkOut AND bEnd > checkIn
                    if (bStart < checkOut && bEnd > checkIn) {
                        isBooked = true;
                        conflictBooking = b;
                        break;
                    }
                }
            }

            return {
                resource: r,
                available: !isBooked,
                conflictBooking: conflictBooking
            };
        });
    }, [resources, bookings, checkIn, checkOut, statusField]);

    var availableCount = useMemo(function () {
        return resourceAvailability.filter(function (r) { return r.available; }).length;
    }, [resourceAvailability]);

    var unavailableCount = useMemo(function () {
        return resourceAvailability.filter(function (r) { return !r.available; }).length;
    }, [resourceAvailability]);

    var hasDateRange = checkIn && checkOut && checkOut > checkIn;

    // Labels
    var checkInLabel = durationType === 'HOURLY'
        ? AntLocalization.translateText({ vi: 'Ngày bắt đầu', en: 'Start date', fr: 'Date de début', es: 'Fecha inicio', id: 'Tanggal mulai', ja: '開始日', zh: '开始日期', ko: '시작일', hi: 'आरंभ तिथि', ar: 'تاريخ البدء' })
        : AntLocalization.translateText({ vi: 'Check-in', en: 'Check-in', fr: 'Arrivée', es: 'Entrada', id: 'Check-in', ja: 'Check-in', zh: '入住', ko: '체크인', hi: 'चेक-इन', ar: 'الوصول' });

    var checkOutLabel = durationType === 'HOURLY'
        ? AntLocalization.translateText({ vi: 'Ngày kết thúc', en: 'End date', fr: 'Date de fin', es: 'Fecha fin', id: 'Tanggal selesai', ja: '終了日', zh: '结束日期', ko: '종료일', hi: 'समाप्ति तिथि', ar: 'تاريخ الانتهاء' })
        : AntLocalization.translateText({ vi: 'Check-out', en: 'Check-out', fr: 'Départ', es: 'Salida', id: 'Check-out', ja: 'Check-out', zh: '退房', ko: '체크아웃', hi: 'चेक-आउट', ar: 'المغادرة' });

    return (
        <div className="range-mode">
            {/* Date range selector */}
            <div className="range-selector">
                <div className="range-field">
                    <label className="range-label">{checkInLabel}</label>
                    <input
                        type="date"
                        className="range-input"
                        value={checkIn ? formatDateISO(checkIn) : ''}
                        onChange={handleCheckInChange}
                    />
                </div>
                <div className="range-arrow">→</div>
                <div className="range-field">
                    <label className="range-label">{checkOutLabel}</label>
                    <input
                        type="date"
                        className="range-input"
                        value={checkOut ? formatDateISO(checkOut) : ''}
                        onChange={handleCheckOutChange}
                        min={checkIn ? formatDateISO(checkIn) : ''}
                    />
                </div>
                {durationLabel && (
                    <div className="range-duration">
                        <span className="duration-value">{durationLabel}</span>
                    </div>
                )}
            </div>

            {/* Results */}
            {!hasDateRange && (
                <div className="range-placeholder">
                    <div className="placeholder-icon">—</div>
                    <div className="placeholder-text">
                        {AntLocalization.translateText({
                            vi: 'Chọn ngày check-in và check-out để xem phòng trống',
                            en: 'Select check-in and check-out dates to see availability',
                            fr: 'Choisissez arrivée et départ pour voir les disponibilités',
                            es: 'Elija fechas de entrada y salida para ver disponibilidad',
                            id: 'Pilih tanggal check-in dan check-out untuk melihat ketersediaan',
                            ja: 'チェックイン日とチェックアウト日を選ぶと空室を表示します',
                            zh: '选择入住和退房日期以查看空房',
                            ko: '체크인·체크아웃 날짜를 선택하면 빈 객실이 표시됩니다',
                            hi: 'उपलब्धता देखने हेतु चेक-इन और चेक-आउट तिथि चुनें',
                            ar: 'اختر تاريخي الوصول والمغادرة لعرض المتاح'
                        })}
                    </div>
                </div>
            )}

            {hasDateRange && (
                <div className="range-results">
                    {/* Summary */}
                    <div className="range-summary">
                        <span className="summary-available">
                            {availableCount} {AntLocalization.translateText({ vi: 'còn trống', en: 'available', fr: 'disponibles', es: 'disponibles', id: 'tersedia', ja: '空き', zh: '可用', ko: '가능', hi: 'उपलब्ध', ar: 'متاح' })}
                        </span>
                        {unavailableCount > 0 && (
                            <span className="summary-unavailable">
                                {unavailableCount} {AntLocalization.translateText({ vi: 'đã đặt', en: 'booked', fr: 'réservés', es: 'ocupados', id: 'dipesan', ja: '予約済', zh: '已订', ko: '예약됨', hi: 'बुक', ar: 'محجوز' })}
                            </span>
                        )}
                    </div>

                    {/* Available resources */}
                    {availableCount > 0 && (
                        <div className="range-section">
                            <div className="section-title section-available">
                                {AntLocalization.translateText({ vi: 'Còn trống', en: 'Available', fr: 'Disponibles', es: 'Disponibles', id: 'Tersedia', ja: '空室', zh: '空闲', ko: '예약 가능', hi: 'उपलब्ध', ar: 'متاحة' })}
                            </div>
                            <div className="resource-list">
                                {resourceAvailability.filter(function (r) { return r.available; }).map(function (r) {
                                    var res = r.resource;
                                    var rCode = res.id || res.code;
                                    var priceText = '';
                                    var totalPriceText = '';
                                    if (res.unit_price) {
                                        var totalDays = daysBetween(checkIn, checkOut);
                                        var total = res.unit_price * totalDays;
                                        priceText = new Intl.NumberFormat('vi-VN').format(res.unit_price);
                                        if (durationType === 'DAILY') {
                                            priceText += '/' + AntLocalization.translateText({ vi: 'đêm', en: 'night', fr: 'nuits', es: 'noches', id: 'malam', ja: '泊', zh: '晚', ko: '박', hi: 'रातें', ar: 'ليالٍ' });
                                        } else if (durationType === 'HOURLY') {
                                            priceText += '/' + AntLocalization.translateText({ vi: 'ngày', en: 'day', fr: 'jour', es: 'día', id: 'hari', ja: '日', zh: '天', ko: '일', hi: 'दिन', ar: 'يوم' });
                                        } else {
                                            priceText += '/' + AntLocalization.translateText({ vi: 'tháng', en: 'month', fr: 'mois', es: 'mes', id: 'bulan', ja: '月', zh: '个月', ko: '개월', hi: 'महीना', ar: 'شهر' });
                                        }
                                        if (totalDays > 0) {
                                            totalPriceText = AntLocalization.translateText({ vi: 'Tổng', en: 'Total', fr: 'Total', es: 'Total', id: 'Total', ja: '合計', zh: '合计', ko: '합계', hi: 'कुल', ar: 'الإجمالي' }) + ': ' + new Intl.NumberFormat('vi-VN').format(total);
                                        }
                                    }

                                    return (
                                        <div key={rCode} className="resource-card available">
                                            <div className="resource-card-info">
                                                <div className="resource-card-icon">
                                                    {res.icon || (res.name ? res.name.charAt(0).toUpperCase() : '—')}
                                                </div>
                                                <div className="resource-card-details">
                                                    <span className="resource-card-name">{res.name || rCode}</span>
                                                    {res.description && (
                                                        <span className="resource-card-desc">{res.description}</span>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="resource-card-right">
                                                {priceText && (
                                                    <div className="resource-card-pricing">
                                                        <span className="resource-card-price">{priceText}</span>
                                                        {totalPriceText && <span className="resource-card-total">{totalPriceText}</span>}
                                                    </div>
                                                )}
                                                <button
                                                    className="resource-book-btn"
                                                    onClick={function () {
                                                        if (onBookResource) {
                                                            onBookResource({
                                                                resource: res,
                                                                resourceId: rCode,
                                                                checkIn: checkIn,
                                                                checkOut: checkOut,
                                                                duration: daysBetween(checkIn, checkOut),
                                                                durationType: durationType || 'DAILY'
                                                            });
                                                        }
                                                    }}
                                                >
                                                    {AntLocalization.translateText({ vi: 'Đặt', en: 'Book', fr: 'Réserv.', es: 'Reserva', id: 'Pesan', ja: '予約', zh: '预订', ko: '예약', hi: 'बुक', ar: 'حجز' })}
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Unavailable resources */}
                    {unavailableCount > 0 && (
                        <div className="range-section">
                            <div className="section-title section-unavailable">
                                {AntLocalization.translateText({ vi: 'Đã đặt', en: 'Booked', fr: 'Réservé', es: 'Reservado', id: 'Dipesan', ja: '予約済み', zh: '已预订', ko: '예약됨', hi: 'बुक किया', ar: 'محجوز' })}
                            </div>
                            <div className="resource-list">
                                {resourceAvailability.filter(function (r) { return !r.available; }).map(function (r) {
                                    var res = r.resource;
                                    var rCode = res.id || res.code;
                                    var conflict = r.conflictBooking;
                                    var guestName = conflict ? (conflict.entity_name || '') : '';
                                    var conflictDates = '';
                                    if (conflict && conflict.check_in_date && conflict.check_out_date) {
                                        conflictDates = formatDate(new Date(conflict.check_in_date)) + ' → ' + formatDate(new Date(conflict.check_out_date));
                                    }

                                    return (
                                        <div
                                            key={rCode}
                                            className={'resource-card unavailable' + (onBookingClick ? ' clickable' : '')}
                                            onClick={function () {
                                                if (onBookingClick && conflict) onBookingClick(conflict);
                                            }}
                                        >
                                            <div className="resource-card-info">
                                                <div className="resource-card-icon dimmed">
                                                    {res.icon || (res.name ? res.name.charAt(0).toUpperCase() : '—')}
                                                </div>
                                                <div className="resource-card-details">
                                                    <span className="resource-card-name dimmed">{res.name || rCode}</span>
                                                    {guestName && (
                                                        <span className="resource-card-guest">
                                                            {AntLocalization.translateText({ vi: 'Khách', en: 'Guest', fr: 'Client', es: 'Cliente', ja: '顧客', zh: '客户', ko: '고객', hi: 'ग्राहक', ar: 'العميل', id: 'Tamu' })}: {guestName}
                                                        </span>
                                                    )}
                                                    {conflictDates && (
                                                        <span className="resource-card-dates">{conflictDates}</span>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="resource-card-right">
                                                <span className="unavailable-badge">
                                                    {AntLocalization.translateText({ vi: 'Đã đặt', en: 'Booked', fr: 'Réservé', es: 'Reservado', id: 'Dipesan', ja: '予約済み', zh: '已预订', ko: '예약됨', hi: 'बुक किया', ar: 'محجوز' })}
                                                </span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
